// Ширины граф таблиц без заданных ширин - как у сборщика (writer/builder.py _col_widths): вес графы =
// max(min(самый длинный текст ячейки, 45), самое длинное слово + 2, 5), ширина = доля веса от поля набора.
// Браузерный table-layout: auto делит ширину иначе, строки ячеек переносятся по-другому, и высота таблицы (а с ней
// разрывы страниц) расходится с Word. В «Разметке страницы» ширины граф ставятся как в DOCX.
import type { EditorView } from '@tiptap/pm/view';
import type { Node as PMNode } from '@tiptap/pm/model';
import { TableMap } from '@tiptap/pm/tables';

function cellText(n: PMNode): string {
  return n.textContent.replace(/[`*$]/g, '');
}

/** Доли ширины граф по правилу writer (null - ширины заданы в документе). */
export function writerWeights(sto: PMNode, table: PMNode): number[] | null {
  if (Array.isArray(sto.attrs.widths) && sto.attrs.widths.length) return null;
  let map: TableMap;
  try { map = TableMap.get(table); } catch { return null; }
  let hasColwidth = false;
  table.descendants((c) => {
    if ((c.type.name === 'tableCell' || c.type.name === 'tableHeader') && Array.isArray(c.attrs.colwidth) && c.attrs.colwidth.some((x: number) => x)) hasColwidth = true;
    return c.type.name === 'table' || c.type.name === 'tableRow';
  });
  if (hasColwidth) return null;
  const n = map.width;
  const longest = new Array(n).fill(0);
  const word = new Array(n).fill(0);
  const seen = new Set<number>();
  for (let i = 0; i < map.map.length; i++) {
    const rel = map.map[i];
    if (seen.has(rel)) continue;
    seen.add(rel);
    const cell = table.nodeAt(rel);
    if (!cell) continue;
    const col = i % n;
    const t = cellText(cell);
    longest[col] = Math.max(longest[col], t.length);
    for (const w of t.split(/\s+/)) word[col] = Math.max(word[col], w.length);
  }
  const weights = longest.map((l, c) => Math.max(Math.min(l, 45), word[c] + 2, 5));
  const tot = weights.reduce((a, b) => a + b, 0) || 1;
  return weights.map((w) => w / tot);
}

/** Поставить ширины граф таблицам без заданных ширин (в DOM; модель не меняется). */
export function syncAutoWidths(view: EditorView): void {
  view.state.doc.descendants((n, pos) => {
    if (n.type.name !== 'stoTable') return n.type.name === 'doc' || n.type.name === 'appendix';
    let tpos = -1;
    let tnode: PMNode | null = null;
    n.forEach((c, off) => { if (c.type.name === 'table') { tpos = pos + 1 + off; tnode = c; } });
    if (!tnode || tpos < 0) return false;
    const fr = writerWeights(n, tnode);
    const dom = view.nodeDOM(tpos) as HTMLElement | null;
    const table = dom ? (dom.tagName === 'TABLE' ? dom : dom.querySelector('table')) as HTMLTableElement | null : null;
    if (!table) return false;
    if (!fr) { table.classList.remove('pg-wcols'); return false; }
    const groups = Array.from(table.querySelectorAll(':scope > colgroup')) as HTMLElement[];
    for (const g of groups) {
      const cols = Array.from(g.children) as HTMLElement[];
      cols.forEach((col, i) => {
        const w = (fr[i % fr.length] * 100).toFixed(4) + '%';
        if (col.style.width !== w) col.style.width = w;
      });
    }
    if (!table.classList.contains('pg-wcols')) table.classList.add('pg-wcols');
    return false;
  });
}
